// Een begrensde zoekboom voor browser en SQL. Alleen expliciete operatoren of
// een groep aan het begin activeren de syntax; gewone bedrijfsnamen blijven tekst.
const QUOTES = new Set(['"', '“', '”', '„']);
// Termen blijven op 32: elke term draait zijn eigen bronnen, dus de tijd groeit mee
// (prod 15 okt 2026, raster kandidaten, titel-OR AND java: 8 termen 6,2 s, 16 termen
// 6,9 s, 32 termen 11,8 s). Tekens naar 2.048, zodat 32 lange titels tussen
// aanhalingstekens uit een Recruiter-string passen.
const MAX_TERMS = 32;
const MAX_CHARS = 2048;
const MAX_DEPTH = 12;

// Veldprefixen voor wie precies wil zoeken (zoekspec U3): `titel:java`,
// `werkgever:"ING Bank"`. Een blad met een veld zoekt alleen in die bron
// (lib/search/keyword-cond.mjs). Een zoekopdracht met een veldprefix is Boolean, ook
// zonder operator: anders zou de NL-parser 'titel:java' als gewone tekst lezen.
export const BOOLEAN_FIELDS = Object.freeze(['titel', 'werkgever', 'cv', 'notitie', 'skill', 'plaats']);
// Engelse namen voor een zoekstring uit LinkedIn Recruiter of een Engelse handleiding:
// ze wijzen naar hetzelfde Nederlandse veld, dus er bestaat maar een bron per veld.
export const BOOLEAN_FIELD_ALIASES = Object.freeze({
  title: 'titel', company: 'werkgever', employer: 'werkgever', note: 'notitie', notes: 'notitie',
  skills: 'skill', location: 'plaats', city: 'plaats', resume: 'cv',
});
const FIELD_RE = new RegExp(`^(${[...BOOLEAN_FIELDS, ...Object.keys(BOOLEAN_FIELD_ALIASES)].join('|')}):`, 'i');

// Operatoren tellen alleen in hoofdletters, net als bij LinkedIn. Een los 'and', 'or'
// of 'not' in kleine letters is een stopwoord: het valt weg in plaats van als tekst te
// zoeken ('java and python' vond 2.694 kandidaten in plaats van 3.254, prod 15 okt 2026).
export const isStopOperator = (word) => /^(and|or|not)$/i.test(word) && !['AND', 'OR', 'NOT'].includes(word);

// Expliciete termen zijn deeltekst, geen SQL-wildcardpatronen.
export const escapeBooleanTerm = (term) => String(term).replace(/[\\%_]/g, '\\$&');
export const booleanLike = (term) => `%${escapeBooleanTerm(term)}%`;

export function parseBooleanQuery(query) {
  const raw = String(query ?? '').trim();
  if (raw.length > MAX_CHARS) {
    const unquoted = raw.replace(/["“”„][^"“”„]*["“”„]/g, ' ');
    const isBoolean = raw.startsWith('(') || /(^|[\s()])(AND|OR|NOT)(?=$|[\s()])/.test(unquoted);
    return { ast: null, isBoolean, error: isBoolean ? 'De zoekopdracht is te lang.' : null, terms: [], fields: [] };
  }
  const tokens = [];
  let error = null;
  let i = 0;
  while (i < raw.length) {
    if (/\s/.test(raw[i])) { i++; continue; }
    const ch = raw[i];
    if (ch === '(' || ch === ')') { tokens.push({ type: ch }); i++; continue; }
    const quoted = () => {
      i++;
      let value = '';
      while (i < raw.length && !QUOTES.has(raw[i])) {
        if (raw[i] === '\\' && i + 1 < raw.length && (QUOTES.has(raw[i + 1]) || raw[i + 1] === '\\')) i++;
        value += raw[i++];
      }
      if (i >= raw.length) error = 'Sluit de aanhalingstekens.';
      else i++;
      return value.replace(/\s+/g, ' ').trim();
    };
    if (QUOTES.has(ch)) {
      tokens.push({ type: 'term', value: quoted() });
      continue;
    }
    const fm = FIELD_RE.exec(raw.slice(i));
    if (fm) {
      i += fm[0].length;
      const field = BOOLEAN_FIELD_ALIASES[fm[1].toLowerCase()] || fm[1].toLowerCase();
      let value = '';
      if (i < raw.length && QUOTES.has(raw[i])) value = quoted();
      else {
        const vs = i;
        while (i < raw.length && !/\s/.test(raw[i]) && raw[i] !== '(' && raw[i] !== ')' && !QUOTES.has(raw[i])) i++;
        value = raw.slice(vs, i);
      }
      if (!value) error = error || 'Vul na een veldprefix een zoekterm in.';
      tokens.push({ type: 'term', value, field });
      continue;
    }
    const start = i;
    while (i < raw.length && !/\s/.test(raw[i]) && raw[i] !== '(' && raw[i] !== ')' && !QUOTES.has(raw[i])) i++;
    const value = raw.slice(start, i);
    if (isStopOperator(value)) continue;
    tokens.push(['AND', 'OR', 'NOT'].includes(value) ? { type: value } : { type: 'term', value });
  }
  const isBoolean = tokens.some((t) => ['AND', 'OR', 'NOT'].includes(t.type) || t.field) || raw.startsWith('(');
  const terms = tokens.filter((t) => t.type === 'term').map((t) => t.value);
  // Per blad het veld (of null), in dezelfde volgorde als terms.
  const fields = tokens.filter((t) => t.type === 'term').map((t) => t.field || null);
  if (!isBoolean) return { ast: null, isBoolean: false, error: null, terms, fields };
  if (raw.length > MAX_CHARS || tokens.length > 128 || terms.length > MAX_TERMS) error = 'De zoekopdracht is te lang.';
  if (error) return { ast: null, isBoolean, error, terms, fields };
  let at = 0;
  const fail = () => { throw new Error('Controleer de operatoren en haakjes.'); };
  const primary = (depth) => {
    if (depth > MAX_DEPTH) throw new Error('De zoekopdracht bevat te veel groepen.');
    const token = tokens[at++];
    if (!token) return fail();
    if (token.type === 'NOT') return { type: 'not', child: primary(depth + 1) };
    if (token.type === '(') {
      const child = or(depth + 1);
      if (tokens[at++]?.type !== ')') return fail();
      return child;
    }
    if (token.type !== 'term' || !token.value) return fail();
    return token.field ? { type: 'term', value: token.value, field: token.field } : { type: 'term', value: token.value };
  };
  const and = (depth) => {
    const children = [primary(depth)];
    while (at < tokens.length && tokens[at].type !== ')' && tokens[at].type !== 'OR') {
      if (tokens[at].type === 'AND') at++;
      children.push(primary(depth));
    }
    return children.length === 1 ? children[0] : { type: 'and', children };
  };
  const or = (depth) => {
    const children = [and(depth)];
    while (tokens[at]?.type === 'OR') { at++; children.push(and(depth)); }
    return children.length === 1 ? children[0] : { type: 'or', children };
  };
  try {
    const ast = or(0);
    if (at !== tokens.length) return fail();
    return { ast, isBoolean, error: null, terms, fields };
  } catch (err) { return { ast: null, isBoolean, error: err.message, terms, fields }; }
}

export function matchBooleanQuery(query, haystack) {
  const parsed = parseBooleanQuery(query);
  const text = String(haystack ?? '').toLocaleLowerCase('nl-NL');
  if (!parsed.isBoolean) return text.includes(String(query ?? '').trim().toLocaleLowerCase('nl-NL'));
  if (parsed.error) return false;
  const walk = (node) => node.type === 'term' ? text.includes(node.value.toLocaleLowerCase('nl-NL'))
    : node.type === 'not' ? !walk(node.child)
      : node.type === 'and' ? node.children.every(walk) : node.children.some(walk);
  return walk(parsed.ast);
}

// leaf bindt zelf de waarde. SQL-null telt als geen treffer, ook onder NOT.
// Derde argument: { field, negated }. negated = het blad staat onder een oneven aantal
// NOT's; een blad met een weggevallen bron geeft daar TRUE, zodat de uitsluiting geen
// vals positieve treffer maakt (lib/search/keyword-cond.mjs).
export function compileBooleanQuery(query, leaf) {
  const parsed = parseBooleanQuery(query);
  if (parsed.error) return 'FALSE';
  let index = 0;
  const walk = (node, negated) => node.type === 'term' ? `COALESCE((${leaf(node.value, index++, { field: node.field || null, negated })}), FALSE)`
    : node.type === 'not' ? `(NOT ${walk(node.child, !negated)})`
      : `(${node.children.map((c) => walk(c, negated)).join(node.type === 'and' ? ' AND ' : ' OR ')})`;
  return parsed.isBoolean ? walk(parsed.ast, false) : `COALESCE((${leaf(String(query ?? '').trim(), 0, { field: null, negated: false })}), FALSE)`;
}
